import { createContext, useContext, useState, useRef } from "react";

const FilterContext = createContext();
export const useFilters = () => useContext(FilterContext);

export function FilterProvider({ children }) {
    const [selectedFilters, setSelectedFilters] = useState({});
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [sort, setSort] = useState("");
    const [products, setProducts] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [isRefetching, setIsRefetching] = useState(false);

    const isClearingRef = useRef(false);

    const apiKeyMap = {
        "Metal Tone": "MetalTone",
        "Gold Quality": "GoldQuality",
        "Price": "Price",
        "Diamond Shape": "DiamondShape",
        "Diamond Quality": "DiamondQuality",
        "By Collection": "Collection",
        "By Item Size": "ItemSize",
        "By Ring Style": "RingStyle"
    };

    const setContextData = ({ products, hasMore }) => {
        setProducts(products);
        setHasMore(hasMore);
        setIsRefetching(false); 
    };

    const updateFilter = (filterName, values) => {
        setSelectedFilters(prev => {
            const next = { ...prev };

            if (values == null || (Array.isArray(values) && values.length === 0)) {
                delete next[filterName];
                return next;
            }

            if (filterName === "Ring Style") {
                if (!values.trim()) {
                    delete next[filterName];
                    return next;
                }
                next[filterName] = values;
                return next;
            }

            if (
                typeof values === "object" &&
                Number.isFinite(values.from) &&
                Number.isFinite(values.to)
            ) {
                next[filterName] = values;
                return next;
            }

            if (Array.isArray(values)) {
                next[filterName] = values;
                return next;
            }

            return next;
        });
    };

    const clearAllFilters = () => {
        isClearingRef.current = true;
        setSelectedFilters({});
        setSelectedCategory(null);
        setTimeout(() => {
            isClearingRef.current = false;
        }, 0);
    };

    const refetchProducts = () => {
        setIsRefetching(true);
        setProducts([]);
        setHasMore(true);
        setRefreshKey(prev => prev + 1); 
    };

    const getFormattedFilters = () => {
        const payload = {};

        Object.keys(selectedFilters).forEach(uiKey => {
            const apiKey = apiKeyMap[uiKey];
            if (!apiKey) return;

            const val = selectedFilters[uiKey];
            if (!val) return;

            if (uiKey === "By Ring Style") {
                payload[apiKey] = val || "";
                return;
            }

            if (Array.isArray(val)) {
                payload[apiKey] = val.map(v => v.value).join("~");
                return;
            }

            if (
                typeof val === "object" &&
                Number.isFinite(val.from) &&
                Number.isFinite(val.to)
            ) {
                payload[apiKey] = `${val.from}~${val.to}`;
            }
        });

        payload.Category = selectedCategory || "";
        payload.Sort = sort || "";
        return payload;
    };

    const markProductInCart = (styleId) => {
        setProducts(prev =>
            prev.map(p =>
                p.Style_ID === styleId
                    ? { ...p, IS_AddedInCart: "YES" }
                    : p
            )
        );
    };

    const markProductInEnquiry = (styleIds) => {
        const ids = Array.isArray(styleIds) ? styleIds : [styleIds];

        setProducts(prev =>
            prev.map(p =>
                ids.includes(p.Style_ID)
                    ? { ...p, IS_AddedInEnquiry: "YES" }
                    : p
            )
        );
    };

    return (
        <FilterContext.Provider
            value={{
                selectedFilters,
                updateFilter,
                selectedCategory,
                setSelectedCategory,
                clearAllFilters,
                products,
                setContextData,
                hasMore,
                sort,
                setSort,
                refreshKey,
                refetchProducts,
                getFormattedFilters,
                markProductInCart,
                markProductInEnquiry,
                isRefetching,
            }}
        >
            {children}
        </FilterContext.Provider>
    );
}
